import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Video, 
  FileText, 
  Clock, 
  CheckCircle2, 
  Circle, 
  Play,
  Image as ImageIcon,
  Table as TableIcon,
  List
} from "lucide-react";
import { cn } from "@/lib/utils";
import { RevealBlock } from "./ScrollRevealSection";
import { EnhancedVideoPlayer } from "./EnhancedVideoPlayer";
import { createSafeHtml } from "@/lib/sanitize";

interface Activity {
  id: string;
  title: string;
  activity_type: string;
  duration_minutes?: number;
  content?: any;
  description?: string;
  is_required?: boolean;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  activities?: Activity[];
}

interface SectionContentRevealProps {
  section: Section;
  sectionNumber: number;
  completedVideos: Set<string>;
  onVideoComplete: (activityId: string) => void;
  onActivityClick: (activity: Activity) => void;
  isActivityCompleted: (activityId: string) => boolean;
  getActivityIcon: (type: string) => any;
}

// Get YouTube video ID helper
function getYouTubeVideoId(url: string) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function SectionContentReveal({
  section,
  sectionNumber,
  completedVideos,
  onVideoComplete,
  onActivityClick,
  isActivityCompleted,
  getActivityIcon
}: SectionContentRevealProps) {
  return (
    <div className="space-y-8 py-4">
      {/* Section Header */}
      <RevealBlock>
        <div className="relative">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 shrink-0">
              <span className="text-lg font-bold text-primary">{sectionNumber}</span>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs sm:hidden">
                  Section {sectionNumber}
                </Badge>
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground leading-tight">
                {section.title}
              </h2>
              {section.description && (
                <div 
                  className="text-muted-foreground prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={createSafeHtml(section.description)}
                />
              )}
            </div>
          </div>
        </div>
      </RevealBlock>

      {/* Section Image */}
      {section.image_url && (
        <RevealBlock delay={100}>
          <Dialog>
            <DialogTrigger asChild>
              <div className="overflow-hidden rounded-xl border shadow-sm cursor-pointer group">
                <img 
                  loading="lazy"
                  src={section.image_url}
                  alt={section.title}
                  className="w-full h-48 sm:h-64 object-contain bg-muted/20 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] max-h-[95vh] p-2">
              <div className="overflow-auto max-h-[90vh]">
                <img 
                  loading="lazy"
                  src={section.image_url}
                  alt={section.title}
                  className="w-full h-auto"
                />
              </div>
            </DialogContent>
          </Dialog>
        </RevealBlock>
      )}

      {/* Activities */}
      <div className="space-y-6">
        {section.activities?.map((activity, index) => {
          const delay = 150 + index * 100;
          const Icon = getActivityIcon(activity.activity_type);

          // Text content with scroll reveal
          if (activity.activity_type === "text" && activity.content?.text) {
            return (
              <RevealBlock key={activity.id} delay={delay}>
                <div className="space-y-4">
                  {activity.title && activity.title.trim() !== "" && activity.title !== "New Text" && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-primary/10">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-medium text-lg">{activity.title}</h3>
                    </div>
                  )}
                  <div className="prose prose-sm max-w-none dark:prose-invert ql-editor bg-gradient-to-br from-muted/30 to-transparent rounded-xl p-5 border border-border/50">
                    <div dangerouslySetInnerHTML={createSafeHtml(activity.content.text)} />
                  </div>
                </div>
              </RevealBlock>
            );
          }

          // Video activities with scroll reveal
          if (activity.activity_type === "video") {
            const isVideoCompleted = completedVideos.has(activity.id);
            const videoUrl = activity.content?.videoUrl;
            const youtubeId = getYouTubeVideoId(activity.content?.youtubeUrl);

            return (
              <RevealBlock key={activity.id} delay={delay}>
                <div className="space-y-4 rounded-xl bg-gradient-to-br from-muted/30 to-transparent border border-border/50 p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-3">
                      {isVideoCompleted ? (
                        <div className="p-2 rounded-xl bg-green-500/10">
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        </div>
                      ) : (
                        <div className="p-2 rounded-xl bg-primary/10">
                          <Video className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      {activity.title && activity.title.trim() !== "" && activity.title !== "New Video" && (
                        <h3 className="font-medium text-base sm:text-lg">{activity.title}</h3>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap ml-11 sm:ml-0">
                      {youtubeId && (
                        <Badge variant="secondary" className="text-xs">YouTube</Badge>
                      )}
                      {activity.duration_minutes && (
                        <Badge variant="secondary" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {activity.duration_minutes}m
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {activity.description && (
                    <p className="text-sm text-muted-foreground pl-11 sm:pl-0">{activity.description}</p>
                  )}
                  
                  <div className="rounded-xl overflow-hidden">
                    {youtubeId ? (
                      <div className="aspect-video w-full">
                        <iframe
                          className="w-full h-full"
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title={activity.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    ) : videoUrl ? (
                      <EnhancedVideoPlayer 
                        url={videoUrl}
                        onEnded={() => onVideoComplete(activity.id)}
                      />
                    ) : (
                      <div className="aspect-video bg-muted/50 rounded-lg flex items-center justify-center border border-dashed border-border">
                        <div className="text-center text-muted-foreground">
                          <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No video uploaded yet</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </RevealBlock>
            );
          }

          // Image activities with scroll reveal
          if (activity.activity_type === "image" && (activity.content?.url || activity.content?.imageUrl)) {
            const imageUrl = activity.content?.imageUrl || activity.content?.url;
            return (
              <RevealBlock key={activity.id} delay={delay}>
                <div className="space-y-4">
                  {activity.title && activity.title.trim() !== "" && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-primary/10">
                        <ImageIcon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-medium text-lg">{activity.title}</h3>
                    </div>
                  )}
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="overflow-hidden rounded-xl border shadow-sm cursor-pointer group">
                        <img 
                          loading="lazy"
                          src={imageUrl}
                          alt={activity.title}
                          className="w-full h-64 object-contain bg-muted/20 group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-[95vw] max-h-[95vh] p-2">
                      <div className="overflow-auto max-h-[90vh]">
                        <img 
                          loading="lazy"
                          src={imageUrl}
                          alt={activity.title}
                          className="w-full h-auto"
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </RevealBlock>
            );
          }

          // Table activities with scroll reveal
          if (activity.activity_type === "table" && activity.content?.columns && activity.content?.rows) {
            return (
              <RevealBlock key={activity.id} delay={delay}>
                <div className="space-y-4">
                  {activity.title && activity.title.trim() !== "" && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-primary/10">
                        <TableIcon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-medium text-lg">{activity.title}</h3>
                    </div>
                  )}
                  <div className="overflow-x-auto border rounded-xl shadow-sm">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-muted/50">
                          {activity.content.columns.map((col: string, colIndex: number) => (
                            <th key={colIndex} className="border-r border-b border-border p-3 text-left text-sm font-semibold last:border-r-0">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activity.content.rows.map((row: string[], rowIndex: number) => (
                          <tr key={rowIndex} className="hover:bg-accent/10 transition-colors">
                            {row.map((cell: string, cellIndex: number) => (
                              <td key={cellIndex} className="border-r border-b border-border p-3 text-sm last:border-r-0 last:border-b-0">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </RevealBlock>
            );
          }

          // Interactive activities (quiz, assignment, etc.)
          if (!['text', 'image', 'table', 'video'].includes(activity.activity_type)) {
            const isCompleted = isActivityCompleted(activity.id);
            
            return (
              <RevealBlock key={activity.id} delay={delay}>
                <div 
                  onClick={() => onActivityClick(activity)}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-5 rounded-xl bg-gradient-to-br from-primary/5 to-transparent border border-primary/20 hover:border-primary/40 hover:shadow-md transition-all duration-300 group cursor-pointer gap-3"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {isCompleted ? (
                      <div className="p-2 rounded-xl bg-green-500/10">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium group-hover:text-primary transition-colors text-base truncate">
                        {activity.title}
                      </p>
                      {activity.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                          {activity.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-11 sm:ml-0">
                    {activity.duration_minutes && (
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {activity.duration_minutes}m
                      </Badge>
                    )}
                    {activity.is_required && (
                      <Badge variant="outline" className="text-xs">Required</Badge>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0 opacity-50 group-hover:opacity-100 transition-opacity"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </RevealBlock>
            );
          }

          return null;
        })}
      </div>

      {/* Section completion indicator */}
      <RevealBlock delay={(section.activities?.length || 0) * 100 + 200}>
        <div className="flex items-center gap-3 pt-4">
          <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
          <CheckCircle2 className="h-4 w-4 text-primary/50" />
          <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
        </div>
      </RevealBlock>
    </div>
  );
}
